import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Mail } from 'lucide-react';

import { db } from '@/lib/db/instant';
import { LogoMark } from '@/components/Logo';
import { cn } from '@/lib/utils/cn';

export const Route = createFileRoute('/sign-in')({
  component: SignInScreen,
});

type Step = 'email' | 'code';

function SignInScreen() {
  const { isLoading, user } = db.useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeRef = useRef<TextInput>(null);

  useEffect(() => {
    if (step === 'code') {
      const t = setTimeout(() => codeRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [step]);

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-bg">
        <ActivityIndicator color="#FF5A1F" />
      </View>
    );
  }
  if (user) return <Navigate to="/home" />;

  const sendCode = async () => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await db.auth.sendMagicCode({ email: e });
      setEmail(e);
      setStep('code');
    } catch (err: any) {
      setError(err?.body?.message || err?.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async () => {
    const c = code.trim();
    if (c.length < 4) {
      setError('Enter the code from your email');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await db.auth.signInWithMagicCode({ email, code: c });
      Keyboard.dismiss();
    } catch (err: any) {
      setError(err?.body?.message || err?.message || 'Invalid code');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SafeAreaView className="flex-1 bg-bg">
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        className="flex-1"
      >
        <View className="flex-1 justify-between px-6 pb-8 pt-6">
          <View>
            <Pressable
              onPress={() => {
                if (step === 'code') {
                  setStep('email');
                  setCode('');
                  setError(null);
                } else {
                  navigate({ to: '/' });
                }
              }}
              hitSlop={12}
              className="mb-8 flex-row items-center gap-1.5 self-start active:opacity-60"
            >
              <ArrowLeft color="#52525B" size={16} />
              <Text className="text-sm text-textDim">
                {step === 'code' ? 'Change email' : 'Back'}
              </Text>
            </Pressable>

            <View className="mb-8 items-start">
              <LogoMark size={48} variant="dark" />
              <Text className="mt-4 text-2xl font-semibold tracking-tight text-text">
                {step === 'email' ? 'Sign in' : 'Check your email'}
              </Text>
              <Text className="mt-1.5 text-sm leading-relaxed text-textDim">
                {step === 'email'
                  ? "We'll email you a one-time code. No password."
                  : `Sent a 6-digit code to ${email}.`}
              </Text>
            </View>

            {step === 'email' ? (
              <View>
                <Text className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-textMuted">
                  Email
                </Text>
                <View className="flex-row items-center gap-2 rounded-xl border border-border bg-surface px-3 py-3">
                  <Mail color="#A1A1AA" size={16} />
                  <TextInput
                    value={email}
                    onChangeText={(t) => {
                      setEmail(t);
                      setError(null);
                    }}
                    placeholder="you@example.com"
                    placeholderTextColor="#A1A1AA"
                    autoCapitalize="none"
                    autoComplete="email"
                    keyboardType="email-address"
                    inputMode="email"
                    returnKeyType="send"
                    onSubmitEditing={sendCode}
                    className="flex-1 text-base text-text outline-none"
                  />
                </View>
              </View>
            ) : (
              <View>
                <Text className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-textMuted">
                  6-digit code
                </Text>
                <TextInput
                  ref={codeRef}
                  value={code}
                  onChangeText={(t) => {
                    setCode(t.replace(/\D/g, '').slice(0, 6));
                    setError(null);
                  }}
                  placeholder="123456"
                  placeholderTextColor="#A1A1AA"
                  keyboardType="number-pad"
                  inputMode="numeric"
                  returnKeyType="done"
                  onSubmitEditing={verifyCode}
                  maxLength={6}
                  className="rounded-xl border border-border bg-surface px-4 py-3.5 text-center text-2xl font-medium tracking-[0.4em] text-text outline-none"
                />
                <Pressable
                  onPress={sendCode}
                  disabled={submitting}
                  className="mt-3 self-start active:opacity-60"
                  hitSlop={8}
                >
                  <Text className="text-xs text-textDim underline">Resend code</Text>
                </Pressable>
              </View>
            )}

            {error && <Text className="mt-3 text-xs text-danger">{error}</Text>}
          </View>

          <View>
            <Pressable
              onPress={step === 'email' ? sendCode : verifyCode}
              disabled={submitting}
              className={cn(
                'items-center rounded-xl bg-accent py-4',
                submitting ? 'opacity-60' : 'active:opacity-80',
              )}
            >
              {submitting ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text className="text-sm font-medium text-white">
                  {step === 'email' ? 'Send code' : 'Sign in'}
                </Text>
              )}
            </Pressable>

            <Text className="mt-4 text-center text-[11px] leading-relaxed text-textMuted">
              By signing in you agree to our{' '}
              <Text
                onPress={() => navigate({ to: '/terms' })}
                className="text-textDim underline"
              >
                Terms
              </Text>
              {' '}and{' '}
              <Text
                onPress={() => navigate({ to: '/privacy' })}
                className="text-textDim underline"
              >
                Privacy Policy
              </Text>
              .
            </Text>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
